import Warning from '@mui/icons-material/Warning';
import { ExclusionList, ValidationList } from '@qbe/ads-blocks-forms';
import { Box, Spacer, Typography } from '@qbe/ads-core';
import React, { useCallback, useEffect } from 'react';

import Modal from '~/components-ds/Modal';
import { useAppContent, useAppDispatch, usePolicyDataBuilder, useSelectCover } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useRTKQErrorHandler } from '~/hooks/useRTKQErrorHandler';
import { useQuotePremiumsMutation } from '~/services/premiums';
import { clearSoftError } from '~/slices/decline';
import { updatePremiums } from '~/slices/quote';
import { addDeclinedCover, removeCover } from '~/slices/selectCover';
import { qalTrackBusinessErrorAction } from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { SoftError } from '~/types/error';
import { isJsonString } from '~/utils/analytics/trackUtils';
import { qalFormatSoftBusinessError } from '~/utils/analytics/types';

interface SoftDeclineModalProps {
  errors: SoftError[];
}

const SoftDeclineModal: React.FC<SoftDeclineModalProps> = ({ errors }) => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent } = useAppContent({ basePath: 'dsme.softDeclineModal' });

  const { useActionText, coversToRemove, coversToDisplay, nextRoute, isLastCover, code, message } =
    errors[0];

  const { validatedCovers, covers } = useSelectCover();
  const { actionText } = useActionText();
  const [quotePremiums, { data, error: quotePremiumsError, isLoading }] =
    useQuotePremiumsMutation();
  const { getPolicyData } = usePolicyDataBuilder({
    covers: validatedCovers.filter((c) => !coversToRemove.includes(c)),
  });
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const declineAndContinue = useCallback(() => {
    coversToRemove.forEach((cover) => {
      dispatch(removeCover(cover));
      dispatch(addDeclinedCover(cover));
    });
    dispatch(clearSoftError());
    dispatch(
      navigateAction({
        to: nextRoute,
        navigate,
      })
    );
  }, [coversToRemove, dispatch, navigate, nextRoute]);

  const coversAvailable = covers.filter((c) => !coversToDisplay.includes(c));

  useRTKQErrorHandler(quotePremiumsError);

  useEffect(() => {
    if (message)
      dispatch(
        qalTrackBusinessErrorAction(
          qalFormatSoftBusinessError({
            code,
            message: isJsonString(message) ? JSON.parse(message)[0]?.message : message,
            covers: coversToDisplay.join(',') || '',
          })
        )
      );
    if (data) {
      dispatch(updatePremiums(data));
      declineAndContinue();
    }
  }, [code, coversToDisplay, data, declineAndContinue, dispatch, message]);

  const onContinue = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    isLastCover ? quotePremiums(getPolicyData()) : declineAndContinue();
  };

  return (
    <Modal
      open
      id="decline-modal"
      title={getTextContent('title')}
      data-track="return to QBE"
      primaryBtnProps={{
        label: isLoading ? getSharedContent('loadingLabel') : actionText,
        onClick: onContinue,
      }}
      secondaryBtnProps={{
        label: getSharedContent('exitButtonLabel'),
        'data-track': 'exit',
        onClick: () => {
          window.location.href = getSharedContent('qbeBusinessInsuranceHomeLink');
        },
      }}
      iconProps={{
        muiIcon: Warning,
        color: '#d43900',
      }}
      // TODO This is a temporary fix as the default which attaches to
      // the body leaves an overflow: hidden on the body element
      container={document.getElementById('root')}
    >
      <Box>
        <Typography>{getTextContent('description')}</Typography>
        <Spacer size="lg" />
        <Typography fontWeight="fontWeightBold" textAlign="left">
          {getTextContent('fields.coversRemoved.title')}
        </Typography>

        <ExclusionList
          list={coversToDisplay.map((cover, idx) => ({
            id: idx,
            item: cover,
          }))}
        />

        {coversAvailable.length ? (
          <>
            <Typography
              sx={{
                fontWeight: 600,
                textAlign: 'left',
              }}
            >
              {getTextContent('fields.coversSelected.title')}
            </Typography>
            <ValidationList
              list={coversAvailable.map((cover, idx) => ({
                id: idx,
                item: cover,
              }))}
            />
          </>
        ) : null}
      </Box>
    </Modal>
  );
};

export default SoftDeclineModal;
